import { motion } from "framer-motion";
import { useEffect } from "react";
import { useShallow } from "zustand/react/shallow";
import { useGameStore, selectCurrentPlayer } from "../../store/gameStore";
import { useLanguage } from "../../i18n/LanguageContext";
import PlayerPanel from "./PlayerPanel";
import RoundTracker from "./RoundTracker";
import TurnIndicator from "./TurnIndicator";
import TurnBanner from "./TurnBanner";
import ActionArea from "./ActionArea";
import CardDetailModal from "../cards/CardDetailModal";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import AdUnit from "../ui/AdUnit";
import { rankPlayers, calcNetWorth } from "../../engine/scoring";

export default function GameBoard() {
  const state = useGameStore(useShallow((s) => s));
  const currentPlayer = useGameStore(selectCurrentPlayer);
  const {
    players, round, totalRounds, costOfLiving, phase,
    showCardModal, showBurnoutModal, showTurnBanner,
    activeDrawnCard, dispatch, resetGame,
  } = state;
  const { t, lang, setLang } = useLanguage();
  const ranked = rankPlayers(players);

  const isMobileOS = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  // On mobile OS: scroll to top whenever the active player changes
  useEffect(() => {
    if (isMobileOS) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [state.currentPlayerIndex, isMobileOS]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 font-display p-3 md:p-4 relative">
      {/* subtle background grid */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(99,102,241,0.08),transparent_60%)]" />

      {/* Header */}
      <div className="flex items-center justify-between mb-4 relative">
        <div className="flex items-center gap-2">
          <div className="text-2xl">💎</div>
          <span className="text-white font-black text-xl tracking-tight">VereV</span>
          <span className="text-white/20 text-xs font-mono ml-1">R{round}/{totalRounds}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setLang(lang === "en" ? "hy" : "en")}
            className="text-white/30 hover:text-white/70 text-sm transition-colors px-2 py-1 rounded-lg hover:bg-white/5"
          >
            {lang === "en" ? "🇦🇲" : "🇬🇧"}
          </button>
          <button
            onClick={resetGame}
            className="text-white/30 hover:text-white/60 text-xs transition-colors px-2 py-1 rounded-lg hover:bg-white/5"
          >
            {t.board.newGame}
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[200px_1fr_200px] gap-3">
        {/* Left: player list */}
        <div className="space-y-2 hidden lg:block">
          <div className="text-white/30 text-xs font-bold tracking-widest px-1 uppercase">{t.board.players}</div>
          {players.map((player, idx) => (
            <PlayerPanel
              key={player.id}
              player={player}
              isActive={idx === state.currentPlayerIndex}
              rank={ranked.findIndex((p) => p.id === player.id)}
            />
          ))}
        </div>

        {/* Center: main play area */}
        <div className="flex flex-col space-y-3">
          {isMobileOS && <ActionArea player={currentPlayer} />}

          <div className="grid grid-cols-2 gap-3">
            <RoundTracker round={round} totalRounds={totalRounds} costOfLiving={costOfLiving} />
            <TurnIndicator player={currentPlayer} phase={phase} />
          </div>

          {/* Mobile: current player panel */}
          <div className="lg:hidden">
            <PlayerPanel player={currentPlayer} isActive />
          </div>

          {!isMobileOS && <ActionArea player={currentPlayer} />}

          <AdUnit />
        </div>

        {/* Right: rankings */}
        <div className="space-y-2 hidden lg:block">
          <div className="text-white/30 text-xs font-bold tracking-widest px-1 uppercase">{t.board.rankings}</div>
          {ranked.map((player, i) => {
            const nw = calcNetWorth(player);
            return (
              <motion.div
                key={player.id}
                className="bg-slate-800/60 rounded-xl border border-white/5 p-2 flex items-center gap-2"
                layout
              >
                <span className="text-white/30 font-bold text-sm w-4">{i + 1}</span>
                <span className="text-lg">{player.avatarKey}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-xs font-bold truncate">{player.name}</div>
                  <div className="text-gold text-xs font-bold">💎{nw}</div>
                </div>
                {i === 0 && <span className="text-xs">👑</span>}
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Card detail modal */}
      <CardDetailModal
        card={activeDrawnCard}
        open={showCardModal}
        onConfirm={() => dispatch({ type: "CONFIRM_CARD" })}
      />

      {/* Burnout modal */}
      <Modal open={showBurnoutModal}>
        <div className="p-6 text-center space-y-4">
          <motion.div
            className="text-6xl"
            animate={{ rotate: [-5, 5, -5] }}
            transition={{ repeat: Infinity, duration: 0.5 }}
          >
            🔥
          </motion.div>
          <div className="font-display font-black text-white text-2xl">{t.modal.burnoutTitle}</div>
          <p className="text-white/60 leading-relaxed">
            <span className="text-white font-bold">{currentPlayer.name}</span>{" "}
            {t.modal.burnoutMsg("").replace(/^[,.\s]+/, "")}
          </p>
          <p className="text-white/40 text-sm">{t.modal.burnoutLesson}</p>
          <Button onClick={() => useGameStore.setState({ showBurnoutModal: false })} size="lg" className="w-full">
            {t.modal.understood}
          </Button>
        </div>
      </Modal>

      {/* Turn banner overlay */}
      <TurnBanner player={currentPlayer} show={showTurnBanner} />
    </div>
  );
}
